import type { CSSObject, Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { focusRing, hoverable, pressable } from '../../interaction-states';
import type { StepperNestedStepState, StepperSize, StepperStepState } from './types';

export const HORIZONTAL_INDICATOR_SIZE = 16;
export const NESTED_INDICATOR_SIZE = 20;
export const SECTION_ICON_SIZE = 16;

export const BAR_HEIGHT: Record<StepperSize, number> = { lg: 8, md: 6 };

type Tone = { fill: string; onFill: string };

export const horizontalTone = (theme: Theme, state: StepperStepState, selected: boolean): Tone => {
  const { colors } = theme;

  switch (state) {
    case 'in-progress':
      return { fill: colors.info.solid, onFill: colors.info.onInfo };
    case 'completed':
      return { fill: colors.info.border, onFill: colors.text.primary };
    case 'error':
      return selected
        ? { fill: colors.error.solid, onFill: colors.error.onError }
        : { fill: colors.error.subtle, onFill: colors.error.onError };
    case 'review':
      return selected
        ? { fill: colors.warning.solid, onFill: colors.warning.onWarning }
        : { fill: colors.warning.subtle, onFill: colors.text.primary };
    default:
      return { fill: colors.background.overlay, onFill: colors.text.primary };
  }
};

export const nestedTone = (theme: Theme, state: StepperNestedStepState): Tone => {
  const { colors } = theme;

  switch (state) {
    case 'pressed':
      return { fill: colors.info.solid, onFill: colors.info.onInfo };
    case 'error':
      return { fill: colors.error.solid, onFill: colors.error.onError };
    default:
      return { fill: colors.background.overlay, onFill: colors.text.primary };
  }
};

const buttonReset: CSSObject = {
  appearance: 'none',
  font: 'inherit',
  color: 'inherit',
  background: 'none',
  border: 'none',
  margin: 0,
  textAlign: 'left',
};

const interactive = (theme: Theme): CSSObject => ({
  cursor: 'pointer',
  ...hoverable(theme, 'surface'),
  ...pressable(theme, 'surfaceStrong'),
  ...focusRing(theme),
});

// Emotion only honours `as` when the filter rejects it; `$`-prefixed props stay off the DOM.
const isTransient = (prop: string) => prop !== 'as' && !prop.startsWith('$');

export const HorizontalRoot = styled('ol')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing[8],
  width: '100%',
  margin: 0,
  padding: 0,
  listStyle: 'none',
}));

export const HorizontalItem = styled('li', { shouldForwardProp: isTransient })<{
  $fit: boolean;
}>(({ $fit }) => ({
  display: 'flex',
  flex: $fit ? '0 0 auto' : 1,
  minWidth: 0,
}));

export const HorizontalStep = styled('button', { shouldForwardProp: isTransient })<{
  $interactive: boolean;
}>(({ theme, $interactive }) => ({
  ...buttonReset,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
  width: '100%',
  minWidth: 0,
  padding: 0,
  borderRadius: theme.radius[4],
  ...($interactive && {
    cursor: 'pointer',
    ...hoverable(theme, 'lift'),
    ...focusRing(theme, { inset: false }),
  }),
}));

export const StepContent = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[8],
  width: '100%',
  minWidth: 0,
}));

export const StepLabel = styled('span', { shouldForwardProp: isTransient })<{
  $noWrap: boolean;
}>(({ $noWrap }) => ({
  flex: 1,
  minWidth: 0,
  ...($noWrap ? { whiteSpace: 'nowrap' } : { overflowWrap: 'anywhere' }),
}));

export const StepIndicator = styled('span', { shouldForwardProp: isTransient })<{
  $size: number;
  $fill: string;
  $onFill: string;
}>(({ theme, $size, $fill, $onFill }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: $size,
  height: $size,
  padding: theme.spacing[2],
  boxSizing: 'border-box',
  borderRadius: theme.radius.full,
  backgroundColor: $fill,
  color: $onFill,

  '& svg': {
    width: '100%',
    height: '100%',
    color: 'inherit',
  },
}));

export const StepBar = styled('div', { shouldForwardProp: isTransient })<{
  $fill: string;
  $height: number;
}>(({ theme, $fill, $height }) => ({
  width: '100%',
  height: $height,
  borderRadius: theme.radius.full,
  backgroundColor: $fill,
}));

export const NestedRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
  padding: theme.spacing[16],
  borderRadius: theme.radius[16],
  backgroundColor: theme.colors.background.raised,
}));

export const SectionList = styled('ul')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
  margin: 0,
  padding: 0,
  listStyle: 'none',
}));

export const SectionItem = styled('li')({
  display: 'flex',
  flexDirection: 'column',
});

export const SectionRow = styled('button', { shouldForwardProp: isTransient })<{
  $locked: boolean;
  $interactive: boolean;
}>(({ theme, $locked, $interactive }) => ({
  ...buttonReset,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[8],
  width: '100%',
  minHeight: 40,
  padding: theme.spacing[8],
  boxSizing: 'border-box',
  borderRadius: theme.radius[8],
  color: $locked ? theme.colors.text.disabled : theme.colors.text.primary,
  ...($interactive && interactive(theme)),
}));

export const SectionIcon = styled('span', { shouldForwardProp: isTransient })<{
  $tone: 'secondary' | 'disabled' | 'warning';
}>(({ theme, $tone }) => ({
  display: 'inline-flex',
  flexShrink: 0,
  width: SECTION_ICON_SIZE,
  height: SECTION_ICON_SIZE,
  color: theme.colors.icon[$tone],

  '& svg': {
    width: '100%',
    height: '100%',
    color: 'inherit',
  },
}));

export const NestedStepList = styled('ul')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[2],
  margin: 0,
  padding: 0,
  paddingLeft: theme.spacing[8],
  listStyle: 'none',
}));

export const NestedStepRow = styled('button', { shouldForwardProp: isTransient })<{
  $interactive: boolean;
}>(({ theme, $interactive }) => ({
  ...buttonReset,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[8],
  width: '100%',
  padding: theme.spacing[8],
  boxSizing: 'border-box',
  borderRadius: theme.radius[8],
  color: theme.colors.text.primary,
  ...($interactive && interactive(theme)),
}));

export const DoneIcon = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  flexShrink: 0,
  width: NESTED_INDICATOR_SIZE,
  height: NESTED_INDICATOR_SIZE,
  color: theme.colors.icon.secondary,

  '& svg': {
    width: '100%',
    height: '100%',
    color: 'inherit',
  },
}));
